import { API } from "@swivl-mono/api-helper";
import { warn } from "@swivl-mono/helpers";
import { GoogleLocation, ReviewGetType, ReviewInterface, ReviewPlatform, ReviewReplyPlatform } from "@swivl-mono/types";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";

interface StoreInterface {
  selectedReviews: { [id: number]: ReviewInterface };
  reviews?: ReviewInterface[];
  archivedReviews?: ReviewInterface[];
  googleLocationsForApp: { [appId: number]: GoogleLocation[] };
}
const BlankState: StoreInterface = {
  selectedReviews: {},
  reviews: undefined,
  archivedReviews: undefined,
  googleLocationsForApp: {},
};

export default class ReviewsModel {
  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = ReviewsModel.useState.setState;
  static get state() {
    return ReviewsModel.useState.getState();
  }
  static reset() {
    ReviewsModel.setState({ ...BlankState });
  }

  static async load(status: ReviewGetType) {
    if (status === ReviewGetType.UNARCHIVED && ReviewsModel.state.reviews) {
      return;
    }
    if (status === ReviewGetType.ARCHIVED && ReviewsModel.state.archivedReviews) {
      return;
    }

    API.Reviews.getReviews(Member.orgId, status)
      .then((reviews) => {
        if (status === ReviewGetType.UNARCHIVED) {
          ReviewsModel.setState({
            reviews: reviews,
          });
        } else {
          ReviewsModel.setState({
            archivedReviews: reviews,
          });
        }
      })
      .catch((e) => {
        warn("Error Loading SMS Group state");
      });
  }

  static loadGoogleLocationsForApp(appId: number, force?: boolean) {
    if (!force && ReviewsModel.state.googleLocationsForApp[appId]) {
      return;
    }
    API.Google.getGoogleAccountAndLocationNames(Member.orgId, appId).then((places) => {
      let googleLocationsForApp = { ...ReviewsModel.state.googleLocationsForApp };
      googleLocationsForApp[appId] = places;
      ReviewsModel.setState({
        googleLocationsForApp: googleLocationsForApp,
      });
    });
  }

  static async replyToReview(review: ReviewInterface, replyText: string) {
    if (!review) {
      return Promise.reject("Missing review");
    }

    if (!review.id) {
      return Promise.reject("Missing review Id");
    }

    API.Reviews.replyToReview(Member.orgId, review.id, {
      replyText: replyText,
      repliedByName: Member.displayName,
      repliedByMemberId: Member.id!,
      replyPlatfrom: ReviewPlatform.SWIVL,
    })
      .then((review) => {
        let reviews = ReviewsModel.state.reviews ? [...ReviewsModel.state.reviews] : [];
        if (reviews) {
          let index = reviews.findIndex((r) => {
            if (!r || !review) {
              return false;
            }
            return r.id === review.id;
          });
          if (index > -1) {
            reviews[index] = review;
          } else {
            reviews.push(review);
          }
        }

        ReviewsModel.setState({
          reviews: reviews,
        });

        return review;
        // ReviewsModel.setState({
        //     reviews : reviews,
        // })
      })
      .catch((e) => {
        warn("Error Loading SMS Group state");
      });
  }

  static async deleteReviewReply(review: ReviewInterface) {
    if (!review) {
      return Promise.reject("Missing review");
    }
    API.Reviews.deleteReviewReply(Member.orgId, review.id)
      .then((review) => {
        let reviews = ReviewsModel.state.reviews ? [...ReviewsModel.state.reviews] : [];

        let index = reviews.findIndex((r) => r.id === review.id);
        if (index > -1) {
          reviews[index] = review;
        } else {
          reviews.push(review);
        }

        ReviewsModel.setState({
          reviews: reviews,
        });
      })
      .catch((e) => {
        warn("Error Loading SMS Group state");
      });
  }

  static async setArchivedOnReviews(reviews: ReviewInterface[], isArchived: boolean) {
    if (!reviews || reviews.length === 0) {
      return Promise.reject("Missing reviews");
    }
    API.Reviews.setArchivedOnReviews(Member.orgId, { ids: reviews.map((r) => r.id), isArchived })
      .then(() => {
        const unarchivedReviews = ReviewsModel.state.reviews ? [...ReviewsModel.state.reviews] : [];
        const archivedReviews = ReviewsModel.state.archivedReviews ? [...ReviewsModel.state.archivedReviews] : [];

        let sourceReviews = isArchived ? unarchivedReviews : archivedReviews;
        let targetReviews = isArchived ? archivedReviews : unarchivedReviews;

        reviews.forEach((review) => {
          review.isArchived = isArchived;
          let index = sourceReviews.findIndex((r) => r.id === review.id);
          if (index > -1) {
            sourceReviews.splice(index, 1);
          }
          index = targetReviews.findIndex((r) => r.id === review.id);
          if (index < 0) {
            targetReviews.push(review);
          }
        });
        if (isArchived) {
          ReviewsModel.setState({
            reviews: unarchivedReviews,
            archivedReviews: archivedReviews,
          });
        } else {
          ReviewsModel.setState({
            reviews: unarchivedReviews,
            archivedReviews: archivedReviews,
          });
        }

        ReviewsModel.deselectReviews(reviews);
      })
      .catch((e) => {
        warn("Error setArchivedOnReviews", e);
      });
  }

  static async setSelectedReviews(selectedReviews: { [id: number]: ReviewInterface }) {
    ReviewsModel.setState({ selectedReviews: selectedReviews });
  }

  static async setSelectOnReview(review: ReviewInterface, shouldSelect: boolean) {
    let selectedReviews = { ...ReviewsModel.state.selectedReviews };

    if (shouldSelect) {
      selectedReviews[review.id] = review;
    } else {
      delete selectedReviews[review.id];
    }
    ReviewsModel.setState({ selectedReviews: selectedReviews });
  }
  static async deselectAllReviews() {
    ReviewsModel.setState({ selectedReviews: {} });
  }
  static async deselectReviews(reviews?: ReviewInterface[]) {
    let selectedReviews = { ...ReviewsModel.state.selectedReviews };
    if (reviews) {
      reviews.forEach((review) => {
        delete selectedReviews[review.id];
      });
    }
    ReviewsModel.setState({ selectedReviews: selectedReviews });
  }

  static async testReply(review: ReviewInterface, replyText: string) {
    return new Promise<ReviewInterface>((resolve, reject) => {
      setTimeout(() => {
        if (Math.floor(Math.random() * 3) === 0) {
          return reject("Error");
        }

        review.replyText = replyText;
        let reviews = ReviewsModel.state.reviews ? [...ReviewsModel.state.reviews] : [];
        if (reviews) {
          let index = reviews.findIndex((r) => r.id === review.id);
          if (index > -1) {
            reviews[index] = review;
          } else {
            reviews.push(review);
          }
        }

        ReviewsModel.setState({
          reviews: reviews,
        });

        resolve(review);
      }, 500);
    });
  }
}
